/**
 * StockBreakdownDialog - Reusable component for showing stock movement breakdown
 *
 * Used in:
 * - StockLevels.js (original implementation)
 * - InventoryRunRateReport.js (MSR sheet)
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Tooltip,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  TrendingUp as InIcon,
  TrendingDown as OutIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getCalculatedStockLevels,
  getTrustPoints,
  getStockBreakdownFromTrustPoint,
  applyTrustPoint,
  previewTrustPoint,
  formatNumber,
  formatDate,
} from '../../services/inventoryService';
import RecordAdjustmentDialog from './RecordAdjustmentDialog';
import RecordReceiptDialog from './RecordReceiptDialog';

// DT-2021: fixed text (never the raw error) for a failed reload after a write. It runs only after the write
// succeeded, so say so: otherwise the user may record the same change again.
const BREAKDOWN_REFRESH_FAILED = 'Your change was saved, but the stock numbers could not be reloaded, so they may be out of date.';

/**
 * StockBreakdownDialog
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Callback when dialog is closed
 * @param {string} props.sku - The SKU to show breakdown for
 * @param {string} props.warehouseCode - The warehouse code (geography for MSR: US, CA, etc.)
 * @param {string} props.productName - Optional product name for display
 * @param {Object} props.warehouseData - Optional pre-loaded warehouse data (for StockLevels)
 * @param {number} props.brandId - Brand ID for API calls
 * @param {string} props.asOfDate - Date for point-in-time calculations
 */
function StockBreakdownDialog({
  open,
  onClose,
  sku,
  warehouseCode,
  productName,
  warehouseData: initialWarehouseData,
  brandId,
  asOfDate,
}) {
  const navigate = useNavigate();

  // Debug: log when component renders
  console.log('[StockBreakdownDialog] Render:', { open, sku, warehouseCode, brandId });

  const [loading, setLoading] = useState(false);
  const [warehouseData, setWarehouseData] = useState(initialWarehouseData || null);
  // Trust points state - grouped by type
  const [trustPoints, setTrustPoints] = useState({
    physical_counts: [],
    goods_receipts: [],
    transfers_in: []
  });
  const [selectedTrustPoint, setSelectedTrustPoint] = useState(null);
  const [loadingTrustPoints, setLoadingTrustPoints] = useState(false);
  const [trustPointsError, setTrustPointsError] = useState(null);
  // Bump this to force the trust-points fetch effect to re-fire (used by the
  // Retry button on the error alert so the user doesn't have to full-page refresh).
  const [trustPointsRefetchKey, setTrustPointsRefetchKey] = useState(0);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  // DT-2021: the reload after a write (Record Adjustment / Receipt, or the apply-trust-point fallback) failed, so the
  // numbers shown are from before that write. Own state: cleared as each such reload starts.
  const [breakdownRefreshError, setBreakdownRefreshError] = useState(null);
  // A reload can still be running when the dialog closes; its late failure must not reappear on the next open.
  const openRef = useRef(open);
  openRef.current = open;
  // Store original data so we can reference it when showing "current stock"
  const [originalQuantity, setOriginalQuantity] = useState(null);
  const [activeTrustPointId, setActiveTrustPointId] = useState(null);
  const [settingTrueStock, setSettingTrueStock] = useState(false);
  const [trueStockResult, setTrueStockResult] = useState(null); // { success, message }
  const [recordAdjustmentOpen, setRecordAdjustmentOpen] = useState(false);
  const [recordReceiptOpen, setRecordReceiptOpen] = useState(false);

  // Trust-point apply form state.
  // overrideQuantity: '' = no override (use the count's recorded value); a numeric
  // string = user-supplied stock at the trust-point date, replaces the count anchor.
  // Pre-fills with the selected count's `quantity` so the user sees what's recorded
  // and can edit it.
  // timing: 'end_of_day' (default) or 'start_of_day'. Only meaningful for
  // physical_count trust points; backend ignores otherwise.
  const [overrideQuantity, setOverrideQuantity] = useState('');
  const [timing, setTiming] = useState('end_of_day');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Fetch warehouse data if not provided
  useEffect(() => {
    const fetchWarehouseData = async () => {
      if (!open || !sku || !warehouseCode) return;
      if (initialWarehouseData) {
        setWarehouseData(initialWarehouseData);
        setOriginalQuantity(initialWarehouseData.quantity);
        return;
      }

      setLoading(true);
      try {
        const params = { limit: 1000 };
        if (brandId) params.brand_id = brandId;
        if (asOfDate) params.as_of_date = asOfDate;

        const response = await getCalculatedStockLevels(params);
        const items = response?.data?.items || [];

        // Find the SKU and warehouse data
        const skuItem = items.find(item => item.primary_sku === sku || item.parent_sku === sku);
        if (skuItem && skuItem.warehouses) {
          const whData = skuItem.warehouses.find(wh => wh.warehouse_code === warehouseCode);
          if (whData) {
            setWarehouseData(whData);
            setOriginalQuantity(whData.quantity);
          }
        }
      } catch (err) {
        console.error('Failed to fetch warehouse data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWarehouseData();
  }, [open, sku, warehouseCode, brandId, asOfDate, initialWarehouseData]);

  // Fetch all trust points (physical counts, goods receipts, transfers in)
  useEffect(() => {
    // Wrap getTrustPoints with one silent auto-retry. The CH cluster occasionally
    // drops a connection mid-request (manifests as a 5xx with 'socket hang up' or
    // ECONNRESET on the backend) — usually due to an unrelated query flooding the
    // pool. The same call almost always succeeds on the next attempt within ~100ms.
    // Without this retry, the user opens the dialog, hits the bad window once, and
    // the error sticks until they manually retry. With it, transient hits are
    // invisible and the dialog "just works" on first open.
    //
    // Only retries on transient-shaped errors (network / 5xx). 4xx errors (auth,
    // validation, not-found) bubble straight through — those are deterministic
    // and a retry won't help.
    const fetchWithRetry = async (sku, warehouseCode, params) => {
      const isTransient = (err) => {
        const status = err?.response?.status;
        const code = err?.code;
        if (status >= 500 && status < 600) return true;
        if (code === 'ECONNABORTED' || code === 'ERR_NETWORK') return true;
        if (!err?.response && err?.message) return true; // network / connection-dropped
        return false;
      };
      try {
        return await getTrustPoints(sku, warehouseCode, params);
      } catch (err) {
        if (!isTransient(err)) throw err;
        console.warn('[StockBreakdownDialog] getTrustPoints transient failure, retrying once:', err?.message);
        await new Promise(resolve => setTimeout(resolve, 250));
        return await getTrustPoints(sku, warehouseCode, params);
      }
    };

    const fetchTrustPoints = async () => {
      if (!open || !sku || !warehouseCode) {
        setTrustPoints({ physical_counts: [], goods_receipts: [], transfers_in: [] });
        setSelectedTrustPoint(null);
        return;
      }

      setLoadingTrustPoints(true);
      setTrustPointsError(null);
      try {
        const params = {};
        if (brandId) params.brand_id = brandId;
        if (asOfDate) params.as_of_date = asOfDate;

        const response = await fetchWithRetry(sku, warehouseCode, params);
        console.log('[StockBreakdownDialog] Trust points response:', response);
        if (response.success && response.data) {
          setTrustPoints(response.data);
          setActiveTrustPointId(response.data.active_trust_point_id || null);
          // Default to the active trust point. The active id can live in any of the
          // three lists (physical_counts / goods_receipts / transfers_in) since
          // applyTrustPoint accepts all three. Backend tells us which list via
          // active_trust_point_type; we look up by id inside that list.
          const activeType = response.data.active_trust_point_type || 'physical_count';
          const activeId = response.data.active_trust_point_id;
          const lists = {
            physical_count: response.data.physical_counts || [],
            goods_receipt: response.data.goods_receipts || [],
            transfer_in: response.data.transfers_in || [],
          };
          const activeTP = activeId != null ? lists[activeType]?.find(tp => tp.id === activeId) : null;
          // Last-resort fallback: any list's most recent entry. Old behavior preferred
          // the most recent physical_count; preserve that for SKUs without an applied
          // trust point yet, and only fall back to other types if no physical_counts exist.
          const fallback = lists.physical_count[0] || lists.goods_receipt[0] || lists.transfer_in[0] || null;
          console.log('[StockBreakdownDialog] Setting default trust point:', activeTP || fallback, '(activeType=', activeType, ')');
          if (activeTP || fallback) setSelectedTrustPoint(activeTP || fallback);
        }
      } catch (err) {
        console.error('Failed to load trust points:', err);
        setTrustPoints({ physical_counts: [], goods_receipts: [], transfers_in: [] });
        // Surface the failure explicitly. Without this, the dialog would silently
        // fall through to the "no physical count at this warehouse" banner — which
        // is a false negative when the API actually has data and we just couldn't
        // fetch it (e.g. transient 500 during a CH restart). Showing an error chip
        // tells the user the failure is recoverable (refresh) rather than a real
        // data state.
        setTrustPointsError(err?.response?.data?.error || err?.message || 'Failed to load trust points');
      } finally {
        setLoadingTrustPoints(false);
      }
    };

    fetchTrustPoints();
    // trustPointsRefetchKey is intentional: bumping it triggers a re-fetch
    // (used by the Retry action on the error alert).
  }, [open, sku, warehouseCode, brandId, asOfDate, trustPointsRefetchKey]);

  // Refetch stock breakdown when selected trust point changes
  // This calls the API endpoint to recalculate movements from the selected trust point
  useEffect(() => {
    console.log('[StockBreakdownDialog] useEffect for selectedTrustPoint triggered:', selectedTrustPoint);

    const fetchBreakdownFromTrustPoint = async () => {
      // Only proceed if we have all required data and a trust point is selected
      const hasTrustPoints = trustPoints.physical_counts.length > 0 ||
                            trustPoints.goods_receipts.length > 0 ||
                            trustPoints.transfers_in.length > 0;

      if (!open || !sku || !warehouseCode || !selectedTrustPoint || !hasTrustPoints) {
        console.log('[StockBreakdownDialog] Early return - missing data:', {
          open, sku, warehouseCode, selectedTrustPoint, hasTrustPoints
        });
        return;
      }

      // Guard A — "Already showing this trust point": if warehouseData was already
      // computed for this selectedTrustPoint.date (via a prior fast path or fetch in
      // this same dialog session), bail out. This prevents overwrites when an unrelated
      // dep re-fires the effect — most notably setOriginalQuantity() inside
      // handleApplyTrustPoint, which would otherwise cause the fast path to trample
      // post-apply fresh data with stale initialWarehouseData.
      if (warehouseData && warehouseData.from_count_date === selectedTrustPoint.date) {
        console.log('[StockBreakdownDialog] warehouseData already windowed to this TP — no-op');
        return;
      }

      // Guard B — "Fast path only when initialWarehouseData is valid for this TP":
      // The main Stock Levels table anchors its cutoff to the active trust point. If the
      // user-selected TP is the active one (id match), initialWarehouseData is the right
      // pre-loaded breakdown — no need to refetch. We allow up to 1 day of date drift
      // between initialWarehouseData.opening_balance_date and selectedTrustPoint.date so
      // that SoD applies (effective_date = event_date - 1) still take the fast path; for
      // staler drift (>1 day, e.g. another session applied a different TP) we fall through
      // to a fresh fetch.
      const dayDrift = (() => {
        if (!initialWarehouseData?.opening_balance_date || !selectedTrustPoint?.date) return null;
        const a = new Date(initialWarehouseData.opening_balance_date).getTime();
        const b = new Date(selectedTrustPoint.date).getTime();
        if (Number.isNaN(a) || Number.isNaN(b)) return null;
        return Math.abs(a - b) / (24 * 60 * 60 * 1000);
      })();
      const canUseInitialSnapshot =
        activeTrustPointId != null &&
        selectedTrustPoint.id === activeTrustPointId &&
        initialWarehouseData &&
        typeof initialWarehouseData.physical_count_qty === 'number' &&
        dayDrift !== null && dayDrift <= 1;

      if (canUseInitialSnapshot) {
        console.log('[StockBreakdownDialog] Active TP matches pre-loaded data — skipping refetch');
        setWarehouseData({
          ...initialWarehouseData,
          current_stock: originalQuantity,
          from_count_date: selectedTrustPoint.date,
          trust_point_type: selectedTrustPoint.type,
        });
        return;
      }

      // Otherwise (non-active TP, stale initialWarehouseData, or no snapshot at all):
      // fetch a windowed breakdown anchored to the chosen date.
      console.log('[StockBreakdownDialog] Calling API for trust point:', selectedTrustPoint);
      setLoadingBreakdown(true);
      try {
        const params = {};
        if (brandId) params.brand_id = brandId;
        if (asOfDate) params.as_of_date = asOfDate;

        // Pass originalQuantity as current_stock so the backend can skip a second heavy query.
        // originalQuantity is the current system stock from the main stock levels page — it hasn't
        // changed (we're just viewing the breakdown from a different trust point, not modifying stock).
        const response = await getStockBreakdownFromTrustPoint(sku, warehouseCode, selectedTrustPoint, params, originalQuantity);
        console.log('[StockBreakdownDialog] API response:', response);
        if (response.success && response.data) {
          // Update warehouse data with the new breakdown
          // Keep the original quantity for display purposes
          setWarehouseData({
            ...response.data,
            // Store original quantity separately for reference
            current_stock: originalQuantity,
          });
        }
      } catch (err) {
        console.error('Failed to fetch stock breakdown from trust point:', err);
        // Keep showing current data on error
      } finally {
        setLoadingBreakdown(false);
      }
    };

    fetchBreakdownFromTrustPoint();
    // Deps intentionally omit activeTrustPointId, initialWarehouseData, and warehouseData:
    // the effect is driven by selectedTrustPoint changes (plus originalQuantity for the
    // post-apply refresh path). All three omitted values are read from the closure at
    // fire time, which captures the latest render's values. Including them would cause
    // unwanted re-fires:
    //   - activeTrustPointId: handleApplyTrustPoint calls setActiveTrustPointId()
    //   - warehouseData: the effect sets warehouseData itself; re-firing on every set
    //     would create noise (Guard A would immediately bail, but it's wasted work)
    //   - initialWarehouseData: rarely changes but is stable per dialog-open session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrustPoint, open, sku, warehouseCode, brandId, asOfDate, trustPoints, originalQuantity]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      if (!initialWarehouseData) {
        setWarehouseData(null);
      }
      setTrustPoints({ physical_counts: [], goods_receipts: [], transfers_in: [] });
      setSelectedTrustPoint(null);
      setActiveTrustPointId(null);
      setTrustPointsError(null);
      setTrustPointsRefetchKey(0);
      setBreakdownRefreshError(null);
      setOriginalQuantity(null);
      setTrueStockResult(null);
      setOverrideQuantity('');
      setTiming('end_of_day');
      setConfirmOpen(false);
      setPreviewData(null);
      setPreviewError(null);
      setPreviewLoading(false);
    }
  }, [open, initialWarehouseData]);

  // Pre-fill the override input + reset timing whenever the user picks a different
  // trust point.
  //   physical_count: pre-fill with the count's recorded quantity (user typically
  //     keeps it; edits only when they have a different known-true value).
  //   goods_receipt / transfer_in: leave blank — receipt/transfer quantity is the
  //     RECEIVED amount, not a stock anchor; user enters their own value if they
  //     want to override (e.g. negative for receipt-day SoD when stock went short).
  useEffect(() => {
    if (selectedTrustPoint && selectedTrustPoint.type === 'physical_count'
        && selectedTrustPoint.quantity != null) {
      setOverrideQuantity(String(selectedTrustPoint.quantity));
    } else {
      setOverrideQuantity('');
    }
    setTiming('end_of_day');
  }, [selectedTrustPoint]);

  // Build the preview/apply payload from current form state. Only includes
  // override_quantity + timing for physical_count trust points where the user
  // either edited the quantity or picked SoD; otherwise sends the original 6-field
  // body so backend takes the existing parallel/windowed path (unchanged behavior).
  const buildTrustPointPayload = () => {
    const p = {
      sku,
      warehouse_code: warehouseCode,
      trust_point_type: selectedTrustPoint.type,
      trust_point_id: selectedTrustPoint.id,
      trust_point_date: selectedTrustPoint.date,
      brand_id: brandId,
    };
    // Override + timing semantics by trust-point type:
    //   physical_count: override defaults to recorded count qty; only send when user
    //     edited it OR explicitly picked SoD timing.
    //   goods_receipt / transfer_in: override is opt-in (no recorded "stock value" to
    //     default to — the receipt/transfer has a quantity_received, not a stock anchor).
    //     Send whenever user provided one or picked SoD timing.
    const overrideNum = overrideQuantity === '' ? null : parseFloat(overrideQuantity);
    const overrideValid = overrideNum != null && !Number.isNaN(overrideNum);
    if (selectedTrustPoint.type === 'physical_count') {
      const recordedQty = parseFloat(selectedTrustPoint.quantity);
      const editedQty =
        overrideValid
        && (Number.isNaN(recordedQty) || Math.abs(overrideNum - recordedQty) > 0.01);
      if (editedQty || timing === 'start_of_day') {
        p.override_quantity = overrideNum;
        p.timing = timing;
      }
    } else {
      // goods_receipt / transfer_in
      if (overrideValid || timing === 'start_of_day') {
        p.override_quantity = overrideValid ? overrideNum : null;
        p.timing = timing;
      }
    }
    return p;
  };

  // Click "Set as True Point" → fetch a read-only preview, open the confirm dialog
  // showing exactly what will change. The actual apply only fires on confirm.
  const handleOpenConfirm = async () => {
    if (!sku || !warehouseCode || !brandId || !selectedTrustPoint) return;
    setPreviewError(null);
    setPreviewData(null);
    setConfirmOpen(true);
    setPreviewLoading(true);
    try {
      const result = await previewTrustPoint(buildTrustPointPayload());
      if (result?.success) {
        setPreviewData(result.data);
      } else {
        setPreviewError(result?.error || 'Preview failed');
      }
    } catch (err) {
      console.error('Failed to preview trust point:', err);
      setPreviewError(err.response?.data?.error || 'Failed to preview trust point');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmApply = async () => {
    setConfirmOpen(false);
    await handleApplyTrustPoint();
  };

  // Handle "Set as True Point" — sends trust point identity to backend,
  // which computes correct vs system stock and creates an additive adjustment
  const handleApplyTrustPoint = async () => {
    if (!sku || !warehouseCode || !brandId || !selectedTrustPoint || !warehouseData) return;

    setSettingTrueStock(true);
    setTrueStockResult(null);
    try {
      const result = await applyTrustPoint(buildTrustPointPayload());
      setTrueStockResult({ success: true, message: result.message || 'Trust point applied successfully' });
      // Mark this trust point as the new active one
      setActiveTrustPointId(selectedTrustPoint.id);

      // Fast path: the backend's applyTrustPoint response now includes `breakdown` — the same
      // shape the GET /breakdown endpoint returns, computed from correctResult during apply.
      // Use it directly and skip the redundant getStockBreakdownFromTrustPoint refetch, which
      // would re-run the same heavy stock query (~5–30s). Only fall back to refetch when the
      // backend didn't provide the breakdown (older deploys or unexpected shape).
      if (result?.data?.breakdown && typeof result.data.breakdown.physical_count_qty === 'number') {
        setWarehouseData({
          ...result.data.breakdown,
          current_stock: result.data.breakdown.quantity,
        });
        setOriginalQuantity(result.data.breakdown.quantity);
        return; // skip refetch
      }

      // Fallback: refetch the breakdown (preserves old behavior if backend didn't include it)
      setLoadingBreakdown(true);
      setBreakdownRefreshError(null);
      try {
        const breakdownParams = {};
        if (brandId) breakdownParams.brand_id = brandId;
        if (asOfDate) breakdownParams.as_of_date = asOfDate;

        const breakdownResponse = await getStockBreakdownFromTrustPoint(
          sku, warehouseCode, selectedTrustPoint, breakdownParams
        );
        if (breakdownResponse.success && breakdownResponse.data) {
          setWarehouseData({
            ...breakdownResponse.data,
            current_stock: breakdownResponse.data.quantity,
          });
          setOriginalQuantity(breakdownResponse.data.quantity);
        }
      } catch (breakdownErr) {
        console.error('Failed to refresh breakdown after trust point apply:', breakdownErr);
        // Fall back to setting quantity from the pre-apply data
        setOriginalQuantity(warehouseData.quantity || 0);
        if (openRef.current) setBreakdownRefreshError(BREAKDOWN_REFRESH_FAILED); // DT-2021: the fallback numbers are pre-apply
      } finally {
        setLoadingBreakdown(false);
      }
    } catch (err) {
      console.error('Failed to apply trust point:', err);
      setTrueStockResult({ success: false, message: err.response?.data?.error || 'Failed to apply trust point' });
    } finally {
      setSettingTrueStock(false);
    }
  };

  // Refetch the breakdown from the currently-selected trust point after an
  // external write (Record Adjustment / Record Receipt). Mirrors the refetch
  // inside handleApplyTrustPoint so the dialog shows the new data immediately.
  const refetchBreakdownAfterWrite = async () => {
    if (!sku || !warehouseCode || !selectedTrustPoint) return;
    setLoadingBreakdown(true);
    setBreakdownRefreshError(null);
    try {
      const params = {};
      if (brandId) params.brand_id = brandId;
      if (asOfDate) params.as_of_date = asOfDate;
      const response = await getStockBreakdownFromTrustPoint(
        sku, warehouseCode, selectedTrustPoint, params
      );
      if (response.success && response.data) {
        setWarehouseData({
          ...response.data,
          current_stock: response.data.quantity,
        });
        setOriginalQuantity(response.data.quantity);
      }
    } catch (err) {
      console.error('Failed to refresh breakdown after write:', err);
      // DT-2021: this used to only log, so the open dialog kept the pre-write numbers with no message.
      if (openRef.current) setBreakdownRefreshError(BREAKDOWN_REFRESH_FAILED);
    } finally {
      setLoadingBreakdown(false);
    }
  };

  const handleViewFullHistory = () => {
    let url = `/inventory/movements?sku=${sku}&warehouse=${warehouseCode}`;
    if (brandId) url += `&brand=${brandId}`;
    if (selectedTrustPoint?.date) {
      url += `&from_count_date=${selectedTrustPoint.date}`;
      url += `&trust_point_type=${selectedTrustPoint.type}`;
      url += `&trust_point_id=${selectedTrustPoint.id}`;
    }
    navigate(url);
    onClose();
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">Stock Breakdown</Typography>
          <Typography variant="body2" color="text.secondary">
            {sku} @ {warehouseCode}
            {productName && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({productName})
              </Typography>
            )}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : warehouseData ? (
          <Box>
            {/* Detect FBA warehouse */}
            {warehouseCode?.endsWith('-FBA') ? (
              /* FBA Warehouse: Simplified view — stock comes from daily Amazon inventory sync */
              <Box>
                {/* Current Stock Summary */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                  <Typography variant="h4" fontWeight="bold" color="primary.main" align="center">
                    {formatNumber(originalQuantity !== null ? originalQuantity : warehouseData.quantity)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Current Stock at {warehouseCode}
                  </Typography>
                </Box>

                {/* Amazon FBA Inventory Source */}
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Stock Source
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InIcon fontSize="small" color="info" />
                        <Typography variant="body2">Amazon FBA Inventory</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="info.main" fontWeight="medium">
                          {formatNumber(warehouseData.fba_stock_qty || warehouseData.quantity || 0)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">Net Stock</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          = {formatNumber(warehouseData.quantity || 0)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                {/* Sync Info */}
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                  <Typography variant="caption" color="info.dark" fontWeight="medium" sx={{ display: 'block' }}>
                    Synced daily from Amazon FBA inventory reports. View Full History for daily stock trend.
                  </Typography>
                </Box>
              </Box>
            ) : (
            /* Internal Warehouse: Full movement breakdown */
            <Box>
            {/* Trust Point Selector - show if any trust points exist */}
            {(() => {
              const totalTrustPoints = (trustPoints.physical_counts?.length || 0) +
                                       (trustPoints.goods_receipts?.length || 0) +
                                       (trustPoints.transfers_in?.length || 0);
              return totalTrustPoints > 1;
            })() && (
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="trust-point-label">Calculate stock from</InputLabel>
                  <Select
                    labelId="trust-point-label"
                    value={selectedTrustPoint ? `${selectedTrustPoint.type}-${selectedTrustPoint.id}` : ''}
                    label="Calculate stock from"
                    onChange={(e) => {
                      const value = e.target.value;
                      console.log('[StockBreakdownDialog] Trust point changed:', value);
                      // Parse the value to find the selected trust point
                      const [type, idStr] = value.split('-');
                      const id = parseInt(idStr);
                      let selected = null;
                      if (type === 'physical_count') {
                        selected = trustPoints.physical_counts.find(tp => tp.id === id);
                      } else if (type === 'goods_receipt') {
                        selected = trustPoints.goods_receipts.find(tp => tp.id === id);
                      } else if (type === 'transfer_in') {
                        selected = trustPoints.transfers_in.find(tp => tp.id === id);
                      }
                      if (selected) {
                        setSelectedTrustPoint(selected);
                        setTrueStockResult(null);
                      }
                    }}
                    disabled={loadingTrustPoints || loadingBreakdown}
                  >
                    {/* Physical Counts Group */}
                    {trustPoints.physical_counts?.length > 0 && (
                      <ListSubheader sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>
                        Physical Counts
                      </ListSubheader>
                    )}
                    {trustPoints.physical_counts?.map((tp) => (
                      <MenuItem key={`physical_count-${tp.id}`} value={`physical_count-${tp.id}`}>
                        {formatDate(tp.date)} - {formatNumber(tp.quantity)} units
                        {activeTrustPointId === tp.id && (
                          <Chip label="Active Trust Point" size="small" color="success" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} />
                        )}
                      </MenuItem>
                    ))}

                    {/* Goods Receipts Group */}
                    {trustPoints.goods_receipts?.length > 0 && (
                      <ListSubheader sx={{ bgcolor: 'success.50', fontWeight: 600, color: 'success.dark' }}>
                        Factory/Supplier Receipts
                      </ListSubheader>
                    )}
                    {trustPoints.goods_receipts?.map((tp) => (
                      <MenuItem key={`goods_receipt-${tp.id}`} value={`goods_receipt-${tp.id}`}>
                        {formatDate(tp.date)} - {formatNumber(tp.quantity)} units ({tp.label})
                      </MenuItem>
                    ))}

                    {/* Transfers In Group */}
                    {trustPoints.transfers_in?.length > 0 && (
                      <ListSubheader sx={{ bgcolor: 'info.50', fontWeight: 600, color: 'info.dark' }}>
                        Transfers In
                      </ListSubheader>
                    )}
                    {trustPoints.transfers_in?.map((tp) => (
                      <MenuItem key={`transfer_in-${tp.id}`} value={`transfer_in-${tp.id}`}>
                        {formatDate(tp.date)} - {formatNumber(tp.quantity)} units ({tp.label})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Loading indicator for breakdown recalculation */}
            {loadingBreakdown && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Recalculating movements from selected trust point...
                </Typography>
              </Box>
            )}

            {/* Current Stock Summary */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
              <Typography variant="h4" fontWeight="bold" color="primary.main" align="center">
                {formatNumber(originalQuantity !== null ? originalQuantity : warehouseData.quantity)}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Current Stock at {warehouseCode}
              </Typography>
            </Box>

            {/* DT-2021: the reload after a write failed; Retry re-runs that same reload. */}
            {breakdownRefreshError && (
              <Alert
                severity="warning"
                sx={{ mb: 1.5, py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}
                action={
                  <Button color="inherit" size="small" onClick={() => refetchBreakdownAfterWrite()} disabled={loadingBreakdown} aria-label="Retry refresh">
                    Retry
                  </Button>
                }
              >
                {breakdownRefreshError}
              </Alert>
            )}

            {/* Active Trust Point indicator */}
            {selectedTrustPoint && activeTrustPointId === selectedTrustPoint.id && (
              <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={`Active Trust Point: ${formatDate(selectedTrustPoint.date)}`}
                  size="small"
                  color="success"
                  sx={{ fontSize: '0.75rem' }}
                />
              </Box>
            )}

            {/* Trust-points fetch error — surface it explicitly so the user knows the
                missing dropdown / banner below isn't a real "no data" state, just a
                transient backend/CH hiccup. Retry button re-fires the same fetch
                without forcing a full-page refresh. */}
            {!loadingTrustPoints && trustPointsError && (
              <Alert
                severity="warning"
                sx={{ mb: 1.5, py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setTrustPointsRefetchKey(k => k + 1)}
                  >
                    Retry
                  </Button>
                }
              >
                Couldn't load trust points ({trustPointsError})
              </Alert>
            )}

            {/* Warning when no physical count exists at this warehouse. Suppressed when
                the fetch errored — that's a false negative, not a real empty state. */}
            {!loadingTrustPoints && !trustPointsError && trustPoints.physical_counts.length === 0 && (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                No physical count at this warehouse — showing all historical movements
              </Alert>
            )}

            {/* Movement Breakdown */}
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Movement Breakdown
            </Typography>
            <Table size="small">
              <TableBody>
                {/* Opening Balance from active trust point (physical_count, goods_receipt, or transfer_in).
                    Gated on opening_balance_date being a real date (not the '1970-01-01' fallback emitted
                    when no anchor exists). The anchor value can be negative when the active trust point is
                    a goods_receipt or transfer_in with a SoD/override (e.g. user declared -38 stuck-label
                    backlog at start of day before a receipt landed) — render with sign so the user can
                    see exactly what was used as the starting point. */}
                {warehouseData.opening_balance_date && warehouseData.opening_balance_date !== '1970-01-01' && (
                  <TableRow>
                    <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InIcon fontSize="small" color="info" />
                      <Typography variant="body2">
                        Opening Balance
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          ({formatDate(warehouseData.opening_balance_date)})
                        </Typography>
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="info.main" fontWeight="medium">
                        {(warehouseData.physical_count_qty || 0) >= 0 ? '+' : ''}{formatNumber(warehouseData.physical_count_qty || 0)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {/* Inflows */}
                <TableRow>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InIcon fontSize="small" color="success" />
                    <Typography variant="body2">Goods Received</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="success.main" fontWeight="medium">
                      +{formatNumber(warehouseData.received_qty || 0)}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InIcon fontSize="small" color="success" />
                    <Typography variant="body2">Transfers In</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="success.main" fontWeight="medium">
                      +{formatNumber(warehouseData.transferred_in_qty || 0)}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InIcon fontSize="small" color="success" />
                    <Typography variant="body2">Returns</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="success.main" fontWeight="medium">
                      +{formatNumber(warehouseData.returned_qty || 0)}
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Divider */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ py: 0.5 }}>
                    <Box sx={{ borderTop: '1px dashed', borderColor: 'divider' }} />
                  </TableCell>
                </TableRow>

                {/* Outflows */}
                <TableRow>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OutIcon fontSize="small" color="error" />
                    <Typography variant="body2">Transfers Out</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={
                        (warehouseData.transferred_out_in_transit_qty || 0) > 0 ? (
                          <Box sx={{ p: 0.5 }}>
                            <Typography variant="caption" display="block">Completed: -{formatNumber((warehouseData.transferred_out_qty || 0) - (warehouseData.transferred_out_in_transit_qty || 0))}</Typography>
                            <Typography variant="caption" display="block">In Transit: -{formatNumber(warehouseData.transferred_out_in_transit_qty || 0)}</Typography>
                          </Box>
                        ) : ''
                      }
                      placement="left"
                      arrow
                    >
                      <Typography variant="body2" color="error.main" fontWeight="medium" sx={{ cursor: (warehouseData.transferred_out_in_transit_qty || 0) > 0 ? 'help' : 'default' }}>
                        -{formatNumber(warehouseData.transferred_out_qty || 0)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OutIcon fontSize="small" color="error" />
                    <Typography variant="body2">FBA Transfers</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="error.main" fontWeight="medium">
                      -{formatNumber(warehouseData.fba_transferred_qty || 0)}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OutIcon fontSize="small" color="error" />
                    <Typography variant="body2">Customer Shipments (ShipStation)</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={
                        (warehouseData.shipped_qty || 0) > 0 ? (
                          <Box sx={{ p: 0.5 }}>
                            <Typography variant="caption" display="block">Individual SKU: -{formatNumber(warehouseData.shipped_direct_qty || 0)}</Typography>
                            <Typography variant="caption" display="block">In Combos: -{formatNumber(warehouseData.shipped_bundle_qty || 0)}</Typography>
                          </Box>
                        ) : ''
                      }
                      placement="left"
                      arrow
                    >
                      <Typography variant="body2" color="error.main" fontWeight="medium" sx={{ cursor: (warehouseData.shipped_qty || 0) > 0 ? 'help' : 'default' }}>
                        -{formatNumber(warehouseData.shipped_qty || 0)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OutIcon fontSize="small" color="error" />
                    <Typography variant="body2">Customer Shipments (Platform)</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={
                        (warehouseData.platform_shipped_qty || 0) > 0 ? (
                          <Box sx={{ p: 0.5 }}>
                            <Typography variant="caption" display="block">Individual SKU: -{formatNumber(warehouseData.platform_shipped_direct_qty || 0)}</Typography>
                            <Typography variant="caption" display="block">In Combos: -{formatNumber(warehouseData.platform_shipped_bundle_qty || 0)}</Typography>
                          </Box>
                        ) : ''
                      }
                      placement="left"
                      arrow
                    >
                      <Typography variant="body2" color="error.main" fontWeight="medium" sx={{ cursor: (warehouseData.platform_shipped_qty || 0) > 0 ? 'help' : 'default' }}>
                        -{formatNumber(warehouseData.platform_shipped_qty || 0)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                {/* Retail/Wholesale Shipments (B2B via Cin7) */}
                <TableRow>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OutIcon fontSize="small" color="error" />
                    <Typography variant="body2">Retail/Wholesale Shipments</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={
                        (warehouseData.retail_shipped_qty || 0) > 0 ? (
                          <Box sx={{ p: 0.5 }}>
                            <Typography variant="caption" display="block">Individual SKU: -{formatNumber(warehouseData.retail_shipped_direct_qty || 0)}</Typography>
                            <Typography variant="caption" display="block">In Combos: -{formatNumber(warehouseData.retail_shipped_bundle_qty || 0)}</Typography>
                          </Box>
                        ) : ''
                      }
                      placement="left"
                      arrow
                    >
                      <Typography variant="body2" color="error.main" fontWeight="medium" sx={{ cursor: (warehouseData.retail_shipped_qty || 0) > 0 ? 'help' : 'default' }}>
                        -{formatNumber(warehouseData.retail_shipped_qty || 0)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OutIcon fontSize="small" color="error" />
                    <Typography variant="body2">Disposals</Typography>
                  </TableCell>
                  <TableCell align="right">
                      <Typography variant="body2" color="error.main" fontWeight="medium">
                        -{formatNumber(warehouseData.disposed_qty || 0)}
                      </Typography>
                  </TableCell>
                </TableRow>

                {/* Adjustments (can be positive or negative) */}
                {(warehouseData.adjustment_qty || 0) !== 0 && (
                  <TableRow>
                    <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {(warehouseData.adjustment_qty || 0) >= 0 ? (
                        <InIcon fontSize="small" color="warning" />
                      ) : (
                        <OutIcon fontSize="small" color="warning" />
                      )}
                      <Typography variant="body2">Adjustments</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={(warehouseData.adjustment_qty || 0) >= 0 ? 'warning.main' : 'error.main'}
                        fontWeight="medium"
                      >
                        {(warehouseData.adjustment_qty || 0) >= 0 ? '+' : ''}
                        {formatNumber(warehouseData.adjustment_qty || 0)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {/* Production consumption (manufacture-PO backflush) */}
                {(warehouseData.production_consumed_qty || 0) !== 0 && (
                  <TableRow>
                    <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <OutIcon fontSize="small" color="secondary" />
                      <Typography variant="body2">Production Consumption</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="error.main" fontWeight="medium">
                        -{formatNumber(warehouseData.production_consumed_qty || 0)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {/* Net Total */}
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">Net Stock</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      = {formatNumber(warehouseData.quantity || 0)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* Set as True Point — hide for the already-active trust point */}
            {selectedTrustPoint && originalQuantity !== null && activeTrustPointId !== selectedTrustPoint.id && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: (warehouseData.quantity || 0) !== originalQuantity ? 'warning.50' : 'grey.50', borderRadius: 1, border: '1px solid', borderColor: (warehouseData.quantity || 0) !== originalQuantity ? 'warning.300' : 'grey.300' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {(warehouseData.quantity || 0) !== originalQuantity
                    ? `Trust-point stock (${formatNumber(warehouseData.quantity || 0)}) differs from system stock (${formatNumber(originalQuantity)}).`
                    : 'Calculate stock from this date as the trust point.'}
                </Typography>

                {/* Override quantity + Start/End-of-day toggle. Available for
                    every trust-point type:
                    - physical_count: defaults to recorded count qty + EoD;
                      user edits to declare a different known-true value.
                    - goods_receipt / transfer_in: empty by default; user enters
                      stock at SoD/EoD of the event date (negative values allowed
                      for receipt-day SoD when warehouse went short pre-receipt). */}
                {selectedTrustPoint && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5, alignItems: { sm: 'flex-end' } }}>
                    <TextField
                      label="Stock at trust point"
                      type="number"
                      size="small"
                      value={overrideQuantity}
                      onChange={(e) => setOverrideQuantity(e.target.value)}
                      // physical_count: non-negative; receipts/transfers can be negative.
                      inputProps={selectedTrustPoint.type === 'physical_count'
                        ? { min: 0, step: 'any' }
                        : { step: 'any' }}
                      sx={{ minWidth: 180 }}
                      helperText={
                        selectedTrustPoint.type === 'physical_count'
                          ? `Recorded count: ${formatNumber(selectedTrustPoint.quantity)}`
                          : selectedTrustPoint.type === 'goods_receipt'
                            ? `Receipt qty: ${formatNumber(selectedTrustPoint.quantity)} — leave blank to anchor at the prior count`
                            : `Transfer qty: ${formatNumber(selectedTrustPoint.quantity)} — leave blank to anchor at the prior count`
                      }
                    />
                    <FormControl size="small">
                      <FormLabel sx={{ fontSize: '0.7rem', mb: 0.25 }}>Timing</FormLabel>
                      <RadioGroup
                        row
                        value={timing}
                        onChange={(e) => setTiming(e.target.value)}
                      >
                        <FormControlLabel
                          value="end_of_day"
                          control={<Radio size="small" />}
                          label={<Typography variant="body2">End of day</Typography>}
                        />
                        <FormControlLabel
                          value="start_of_day"
                          control={<Radio size="small" />}
                          label={<Typography variant="body2">Start of day</Typography>}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Stack>
                )}

                <Button
                  variant="contained"
                  size="small"
                  color="warning"
                  onClick={handleOpenConfirm}
                  disabled={settingTrueStock || previewLoading}
                  sx={{ textTransform: 'none' }}
                >
                  {settingTrueStock ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                  Set as True Point ({formatDate(selectedTrustPoint.date)})
                </Button>
                {trueStockResult && (
                  <Typography
                    variant="caption"
                    color={trueStockResult.success ? 'success.main' : 'error.main'}
                    sx={{ display: 'block', mt: 1 }}
                  >
                    {trueStockResult.message}
                  </Typography>
                )}
              </Box>
            )}

            {/* Record Adjustment / Record Receipt — always available */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                onClick={() => setRecordAdjustmentOpen(true)}
                sx={{ textTransform: 'none', flex: 1 }}
              >
                Record Adjustment
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="success"
                onClick={() => setRecordReceiptOpen(true)}
                sx={{ textTransform: 'none', flex: 1 }}
              >
                Record Receipt
              </Button>
            </Box>

            {/* Stock Count History Summary */}
            {(warehouseData.total_count_count || 0) > 0 && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="caption" color="text.secondary" fontWeight="medium" sx={{ display: 'block', mb: 1 }}>
                  Stock Count History
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    * Total counts: {warehouseData.total_count_count}
                  </Typography>
                  {warehouseData.last_stock_count_date && (
                    <Typography variant="caption" color="text.secondary">
                      * Last count: {formatDate(warehouseData.last_stock_count_date)}
                    </Typography>
                  )}
                  {(warehouseData.last_reported_stock || 0) > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      * Last reported: {formatNumber(warehouseData.last_reported_stock)} units
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}

          </Box>
          )}
          </Box>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No stock data available for {sku} at {warehouseCode}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={handleViewFullHistory}
        >
          View Full History
        </Button>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* Sibling dialogs — triggered by the Record Adjustment / Record Receipt buttons */}
    <RecordAdjustmentDialog
      open={recordAdjustmentOpen}
      onClose={() => setRecordAdjustmentOpen(false)}
      sku={sku}
      warehouseCode={warehouseCode}
      currentQuantity={originalQuantity !== null ? originalQuantity : (warehouseData?.quantity || 0)}
      brandId={brandId}
      onSuccess={refetchBreakdownAfterWrite}
    />
    <RecordReceiptDialog
      open={recordReceiptOpen}
      onClose={() => setRecordReceiptOpen(false)}
      sku={sku}
      warehouseCode={warehouseCode}
      currentQuantity={originalQuantity !== null ? originalQuantity : (warehouseData?.quantity || 0)}
      brandId={brandId}
      onSuccess={refetchBreakdownAfterWrite}
    />

    {/* Confirmation dialog — shown after clicking "Set as True Point". Loads a
        read-only preview from the backend (which existing trust-point adjustments
        get deleted, what variance gets inserted) so the user sees exactly what's
        about to change before they confirm. */}
    <Dialog
      open={confirmOpen}
      onClose={() => !settingTrueStock && setConfirmOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Confirm Trust Point</DialogTitle>
      <DialogContent dividers>
        {previewLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress size={24} sx={{ mr: 1.5 }} />
            <Typography variant="body2" color="text.secondary">Computing impact…</Typography>
          </Box>
        ) : previewError ? (
          <Alert severity="error">{previewError}</Alert>
        ) : previewData ? (
          <Box>
            {/* Stock impact summary — all values are at the trust-point moment with the
                user's timing choice. SoD on count_date is implemented as EoD on the
                previous day (the effective cutoff), but we LABEL it as "start of day,
                count_date" to match the user's mental model. */}
            {(() => {
              const isPhysicalCount = previewData.trust_point_type === 'physical_count';
              const hasOverride = previewData.override_quantity != null;
              const isSoD = previewData.timing === 'start_of_day';
              // Anchor-at-event semantics:
              //   physical_count: anchored at the count date (with SoD shift if applicable).
              //   goods_receipt / transfer_in WITH override: anchored at the event date too.
              //   goods_receipt / transfer_in WITHOUT override: anchored at the prior count
              //     (effective_cutoff_date) — no SoD/EoD timing applies.
              const anchorAtEvent = isPhysicalCount || hasOverride;
              const timingPrefix = anchorAtEvent ? (isSoD ? 'start of day, ' : 'end of day, ') : '';
              const anchorDateStr = formatDate(
                anchorAtEvent ? previewData.trust_point_date : previewData.effective_cutoff_date
              );
              const atMoment = `${timingPrefix}${anchorDateStr}`;
              // Read at_tp values; fall back to legacy field names for backward compat.
              const correctAtTp = previewData.correct_stock_at_tp != null
                ? previewData.correct_stock_at_tp
                : previewData.correct_stock;
              const systemAtTp = previewData.system_stock_at_tp != null
                ? previewData.system_stock_at_tp
                : previewData.system_stock;
              return (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Stock impact at {atMoment}
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ borderBottom: 'none', py: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">Trust point</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: 'none', py: 0.5 }}>
                          <Typography variant="body2">
                            {formatDate(previewData.trust_point_date)}
                            {anchorAtEvent && (
                              <Chip
                                label={isSoD ? 'Start of day' : 'End of day'}
                                size="small"
                                sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {isSoD && previewData.effective_cutoff_date && (
                        <TableRow>
                          <TableCell sx={{ borderBottom: 'none', py: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              Internally evaluated as end of day, {formatDate(previewData.effective_cutoff_date)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ borderBottom: 'none', py: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              (start-of-day = previous day's end)
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell sx={{ borderBottom: 'none', py: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            System stock at {atMoment}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: 'none', py: 0.5 }}>
                          <Typography variant="body2">{formatNumber(systemAtTp)}</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ borderBottom: 'none', py: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            {previewData.override_quantity != null
                              ? `Your declared stock at ${atMoment}`
                              : `Recorded count at ${atMoment}`}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: 'none', py: 0.5 }}>
                          <Typography variant="body2" fontWeight={500}>{formatNumber(correctAtTp)}</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography variant="body2" fontWeight={600}>
                            Discrepancy at {atMoment}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5 }}>
                          <Typography variant="body2" fontWeight={600} color={previewData.variance === 0 ? 'text.secondary' : (previewData.variance > 0 ? 'success.main' : 'error.main')}>
                            {previewData.variance > 0 ? '+' : ''}{formatNumber(previewData.variance)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              );
            })()}

            <Divider sx={{ my: 1.5 }} />

            {/* Existing adjustments that will be deleted */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Existing trust-point adjustments to remove
                {previewData.existing_adjustments_to_delete.length > 0 && (
                  <Chip
                    label={previewData.existing_adjustments_to_delete.length}
                    size="small"
                    color="warning"
                    sx={{ ml: 1, height: 18 }}
                  />
                )}
              </Typography>
              {previewData.existing_adjustments_to_delete.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No existing trust-point adjustments for this SKU/warehouse — nothing will be deleted.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 0.5 }}>Date</TableCell>
                      <TableCell sx={{ py: 0.5 }} align="right">Variance</TableCell>
                      <TableCell sx={{ py: 0.5 }} align="right">Override</TableCell>
                      <TableCell sx={{ py: 0.5 }}>Applied at</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.existing_adjustments_to_delete.map((adj) => (
                      <TableRow key={adj.id}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography variant="caption">{formatDate(adj.effective_date)}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }} align="right">
                          <Typography variant="caption" color={adj.variance > 0 ? 'success.main' : (adj.variance < 0 ? 'error.main' : 'text.secondary')}>
                            {adj.variance > 0 ? '+' : ''}{formatNumber(adj.variance)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }} align="right">
                          <Typography variant="caption">
                            {adj.anchor_quantity != null ? formatNumber(adj.anchor_quantity) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {adj.adjusted_at ? String(adj.adjusted_at).slice(0, 16) : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            {previewData.would_insert == null && (
              <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }}>
                Variance is effectively zero — no new adjustment will be inserted, but any existing trust-point adjustments above will still be removed.
              </Alert>
            )}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmOpen(false)} disabled={settingTrueStock}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleConfirmApply}
          disabled={previewLoading || settingTrueStock || !!previewError || !previewData}
        >
          {settingTrueStock ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          Confirm &amp; Apply
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

export default StockBreakdownDialog;
